import { Fragment, useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import flag from '~/assets/icons/flag.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import {
	IPlayerSearchResult,
	rage,
	useInputAction,
	useObservable,
} from '~/rage';
import { Currency, InputAction, ModalButtonColor, ModalType } from '~/shared';
import {
	setActiveMenu,
	setSelectedPlayer,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Head } from '../Head';
import { MoneyInput } from '../MoneyInput';
import {
	StyledGovSellContainer,
	StyledGovSellPriceContainer,
	StyledMoneyInputContainer,
	StyledNoPlayersContainer,
	StyledPlayerContainer,
	StyledPlayerList,
	StyledTypeButtonsContainer,
} from './SellMenu.styled';

const SellMenu: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const houseData = useLayoutSelector((state) => state.homeBuying.houseData);
	const selectedPlayer = useLayoutSelector(
		(state) => state.homeBuying.selectedPlayer,
	);
	const [players, setPlayers] = useState<IPlayerSearchResult[]>([]);
	const [moneyValue, setMoneyValue] = useState<number>(0);
	const inputFocused = useLayoutSelector(
		(state) => state.homeBuying.inputFocused,
	);
	const [sellMode, setSellMode] = useState<'player' | 'gov'>('player');
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const handleSelect = useCallback((player: IPlayerSearchResult) => {
		dispatch(setSelectedPlayer(player));
	}, []);

	const handleSellPlayer = useCallback(async () => {
		if (!houseData || modalState) {
			return;
		}

		await rage.houses.inviteToBuyHouse(
			houseData?.id || '',
			selectedPlayer.characterId,
			moneyValue,
		);
		dispatch(setActiveMenu('home'));
	}, [houseData, moneyValue, selectedPlayer, modalState]);

	const handleSellGov = useCallback(() => {
		if (!houseData || modalState) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				price: houseData?.sellPrice || 0,
				text: `Вы действительно хотите продать дом #${houseData?.id} государству?`,
				acceptButton: {
					text: 'Продать гос-ву',
					color: ModalButtonColor.Success,
				},
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result) => {
				if (result.isAccepted) {
					rage.houses
						.sell(houseData?.id || '')
						.catch((err) => rage.notifications.showException(err));
					dispatch(setActiveMenu('home'));
				}
			},
		);
	}, [houseData, modalState]);

	useInputAction(InputAction.HomeBuyingBack, () => {
		if (inputFocused) {
			return;
		}
		dispatch(setActiveMenu('home'));
	});

	useEffectOnce(() => {
		rage.search
			.findNearbyPlayers()
			.then((filteredPlayers) => {
				setPlayers(filteredPlayers);
			})
			.catch((err) => rage.notifications.showException(err));

		return () => {
			dispatch(setSelectedPlayer({ characterId: '', nickname: '' }));
		};
	});

	return (
		<div>
			<Head />
			<Divider
				$direction="H"
				style={{ marginTop: 20, marginBottom: 30, opacity: 0.1 }}
			/>
			<SectionHeading icon={flag} text="Выберите тип продажи" />
			<StyledTypeButtonsContainer>
				<Button
					color={
						sellMode === 'player' ? ButtonColors.Select2 : ButtonColors.Default
					}
					height={ButtonHeight.Height50}
					onClick={() => setSellMode('player')}
				>
					<Text>Продать игроку</Text>
				</Button>
				<Button
					color={
						sellMode === 'gov' ? ButtonColors.Select2 : ButtonColors.Default
					}
					height={ButtonHeight.Height50}
					onClick={() => setSellMode('gov')}
				>
					<Text>Продать в гос.</Text>
				</Button>
			</StyledTypeButtonsContainer>
			<Divider $direction="H" style={{ marginTop: 20, opacity: 0.1 }} />
			{sellMode === 'player' && (
				<Fragment>
					<ScrollContainer height={220} style={{ marginTop: 20 }}>
						<StyledPlayerList tabIndex={-1}>
							{players.length === 0 && (
								<StyledNoPlayersContainer>
									<Text
										style={{ fontSize: 16, color: '#ffffff', opacity: 0.25 }}
									>
										Поблизости нет игроков
									</Text>
								</StyledNoPlayersContainer>
							)}
							{players.map((item, index) => (
								<Fragment key={index}>
									<StyledPlayerContainer $first={index === 0}>
										<Text>{item.nickname}</Text>
										<Button
											color={
												item.characterId === selectedPlayer.characterId
													? ButtonColors.Help
													: ButtonColors.Default
											}
											height={ButtonHeight.Height30}
											onClick={() => handleSelect(item)}
											style={{
												width: 'fit-content',
												fontSize: 16,
												padding: '0 10px',
											}}
										>
											<Text>
												{item.characterId === selectedPlayer.characterId
													? 'Выбрано'
													: 'Выбрать'}
											</Text>
										</Button>
									</StyledPlayerContainer>
									{index !== players.length - 1 && (
										<Divider $direction="H" style={{ opacity: 0.1 }} />
									)}
								</Fragment>
							))}
						</StyledPlayerList>
					</ScrollContainer>
					<Divider $direction="H" style={{ opacity: 0.1, marginTop: 20 }} />
					<StyledMoneyInputContainer>
						<MoneyInput value={moneyValue} onChange={setMoneyValue} />
					</StyledMoneyInputContainer>
					<Button
						color={ButtonColors.Buy}
						height={ButtonHeight.Height50}
						disabled={!selectedPlayer.characterId || moneyValue <= 0}
						onClick={handleSellPlayer}
						style={{ marginTop: 30 }}
					>
						<Text>
							{!selectedPlayer.characterId
								? 'Выберите игрока из списка'
								: moneyValue > 0
								? 'Продать недвижимость'
								: 'Введите сумму продажи'}
						</Text>
					</Button>
				</Fragment>
			)}
			{sellMode === 'gov' && (
				<Fragment>
					<StyledGovSellContainer>
						<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.5 }}>
							Вы можете продать свое имущество государству и&nbsp;получить за
							него 50% от рыночной стоимости.
						</Text>
					</StyledGovSellContainer>
					<Divider $direction="H" style={{ opacity: 0.1 }} />
					<StyledGovSellPriceContainer>
						<Text style={{ fontSize: 18, color: '#ffffff', opacity: 0.25 }}>
							Стоимость продажи
						</Text>
						<Text style={{ fontSize: 18, color: '#ffffff', fontWeight: 700 }}>
							{houseData?.sellPrice.toLocaleString('ru-RU')}
						</Text>
						<CurrencyIcon icon={rubleIcon} size="sm" />
					</StyledGovSellPriceContainer>
					<Button
						color={ButtonColors.Buy}
						height={ButtonHeight.Height50}
						onClick={handleSellGov}
						style={{ marginTop: 30 }}
					>
						<Text>Продать дом государству</Text>
					</Button>
				</Fragment>
			)}
			<Button
				color={ButtonColors.Default}
				height={ButtonHeight.Height50}
				onClick={() => dispatch(setActiveMenu('home'))}
				style={{ marginTop: 10 }}
			>
				<Text>Отменить продажу</Text>
			</Button>
		</div>
	);
};

export { SellMenu };
